/* ==========================================================================
   DH Reviews Scroll — Scroll-driven fan animation  v7

   Desktop (1200px+): Element's scroll-through-viewport progress drives
     card transforms: stacked → fan out → hold.
     Cards start overlapping at center (only image card visible),
     then fan into a downward arc as the user scrolls.
     Smoothed with exponential interpolation via rAF loop.
   Tablet (600-1199px): Static fan applied once.
   Mobile (<600px): Native CSS carousel with dot indicators.
   ========================================================================== */

if (!customElements.get('dh-reviews-scroll')) {
  class DhReviewsScroll extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-dh-rs-track]');
      this.dotsContainer = this.querySelector('[data-dh-rs-dots]');
      this.headlineEl = this.querySelector('.dh-rs__headline');

      if (!this.track) return;

      this.cards = Array.from(this.track.querySelectorAll('[data-dh-rs-card]'));
      this.dots = this.dotsContainer
        ? Array.from(this.dotsContainer.querySelectorAll('[data-dh-rs-dot]'))
        : [];

      this.cardCount = this.cards.length;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mqDesktop = window.matchMedia('(min-width: 1200px)');
      this.mqTablet = window.matchMedia('(min-width: 600px)');

      this._rafId = null;
      this._scrollHandler = null;
      this._resizeHandler = null;
      this._carouselObserver = null;
      this._hoverHandlers = [];
      this._dotHandlers = [];
      this._activeDotIndex = 0;

      // Desktop scroll wrapper elements
      this._scrollOuter = null;
      this._stickyWrap = null;

      // Smoothing state
      this._smoothedProgress = 0;
      this._rawProgress = 0;
      this._isAnimating = false;

      this._onBreakpointChange = this._init.bind(this);
      this._onSectionEvent = this._handleEditorEvent.bind(this);

      this.mqDesktop.addEventListener('change', this._onBreakpointChange);
      this.mqTablet.addEventListener('change', this._onBreakpointChange);
      document.addEventListener('shopify:section:load', this._onSectionEvent);
      document.addEventListener('shopify:section:select', this._onSectionEvent);

      this._init();
    }

    disconnectedCallback() {
      this._teardown();
      if (this.mqDesktop) this.mqDesktop.removeEventListener('change', this._onBreakpointChange);
      if (this.mqTablet) this.mqTablet.removeEventListener('change', this._onBreakpointChange);
      document.removeEventListener('shopify:section:load', this._onSectionEvent);
      document.removeEventListener('shopify:section:select', this._onSectionEvent);
    }

    _handleEditorEvent(e) {
      if (e && e.target && !e.target.contains(this)) return;
      this._init();
    }

    _teardown() {
      this._isAnimating = false;
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler);
        this._scrollHandler = null;
      }
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
        this._resizeHandler = null;
      }
      if (this._carouselObserver) {
        this._carouselObserver.disconnect();
        this._carouselObserver = null;
      }
      if (this._carouselScrollHandler && this.track) {
        this.track.removeEventListener('scroll', this._carouselScrollHandler);
        this._carouselScrollHandler = null;
      }
      if (this._scrollTimer) {
        cancelAnimationFrame(this._scrollTimer);
        this._scrollTimer = null;
      }
      this._hoverHandlers.forEach(function (h) {
        h.el.removeEventListener('mouseenter', h.enter);
        h.el.removeEventListener('mouseleave', h.leave);
      });
      this._hoverHandlers = [];
      this._dotHandlers.forEach(function (h) {
        h.el.removeEventListener('click', h.handler);
      });
      this._dotHandlers = [];

      // Unwrap scroll-outer if created
      this._unwrapScrollOuter();

      // Reset card inline styles
      this.cards.forEach(function (card) {
        card.style.transform = '';
        card.style.zIndex = '';
        card._dhHovered = false;
      });

      this._stackOffsets = [];
    }

    /* ==================================================================
       Init: Pick the right mode
       ================================================================== */

    _init() {
      if (!this.track || this.cardCount === 0) return;
      this._teardown();
      this._buildKeyframes();

      if (this.cardCount <= 1) {
        return;
      }

      if (this.mqDesktop.matches && !this.prefersReducedMotion) {
        this._setupDesktopScroll();
      } else if (this.mqTablet.matches) {
        this._setupStaticFan();
      } else {
        this._setupMobileCarousel();
      }
    }

    /* ==================================================================
       Keyframes — dynamically computed for any card count

       Cards are in a CSS grid. We compute how far each card's grid
       cell center is from the track center (in vw), so the "stacked"
       state can bring all cards to the center point.

       Two states:
       - stacked: all cards overlapping at center (only image visible)
       - fan: cards in a downward arc with rotation

       Fan reference (5 cards, outer offset = 2 from center):
         outer: ±19.3vw, 25vh, ±25deg
         inner: ±9.65vw, 12.5vh, ±12.5deg
         center: 0, 6.25vh, 0deg
       ================================================================== */

    _buildKeyframes() {
      var count = this.cardCount;
      this._keyframes = [];

      // Fan rotation per unit offset from center
      var fanRotPerUnit = 12;   // deg

      var centerIdx = (count - 1) / 2;

      for (var i = 0; i < count; i++) {
        var offset = i - centerIdx;
        var absOffset = Math.abs(offset);

        // Fan rotation
        var fanR = offset * fanRotPerUnit;

        // Z-index: center highest, outer lowest
        var z = absOffset < 0.01 ? 100 : Math.round(50 - absOffset * 10);

        this._keyframes.push({
          fanR: fanR,
          z: z
        });
      }
    }

    /* ==================================================================
       Compute stack offsets — measures each card's distance from
       track center so we can bring them all to one point.
       Called once at setup and on resize.
       ================================================================== */

    _computeStackOffsets() {
      this._stackOffsets = []; // px to bring each card to track center
      this._fanOffsets = [];   // px X offset for fan (partial spread from center)
      var trackRect = this.track.getBoundingClientRect();
      var trackCenterX = trackRect.left + trackRect.width / 2;

      for (var i = 0; i < this.cardCount; i++) {
        var cardRect = this.cards[i].getBoundingClientRect();
        var cardCenterX = cardRect.left + cardRect.width / 2;
        var distFromCenter = cardCenterX - trackCenterX; // positive = right of center

        // Stack offset: negate the distance to bring card to center
        this._stackOffsets.push(-distFromCenter);

        // Fan offset: 40% of the distance from center (tighter than grid, looser than stacked)
        this._fanOffsets.push(-distFromCenter * 0.6);
      }

      // Fan Y: compute based on distance from center, in px
      this._fanYOffsets = [];
      var centerIdx = (this.cardCount - 1) / 2;
      for (var j = 0; j < this.cardCount; j++) {
        var absOff = Math.abs(j - centerIdx);
        // Outer cards drop more; center drops a tiny bit
        this._fanYOffsets.push(absOff < 0.01 ? 10 : (10 + absOff * 30));
      }
    }

    /* ==================================================================
       Desktop: Scroll-progress-driven animation with smoothing
       ================================================================== */

    /* ==================================================================
       Wrap/Unwrap: Desktop needs a tall scroll-outer + sticky container
       so the cards stay viewport-centered during the scroll animation.
       ================================================================== */

    _wrapWithScrollOuter() {
      if (this._scrollOuter) return;

      var header = this.querySelector('.dh-rs__header');
      var cta = this.querySelector('.dh-rs__cta');
      var section = this.track.closest('section');
      if (!section) return;

      // Create: scroll-outer > sticky > (header + track + cta)
      // Negative margin pulls scroll-outer top above visible content,
      // so sticky engages while content is mid-viewport (not at top).
      // Padding-top compensates so visible content stays in place.
      this._scrollOuter = document.createElement('div');
      this._scrollOuter.className = 'dh-rs__scroll-outer';
      this._scrollOuter.style.height = '210vh';
      this._scrollOuter.style.marginTop = '-50vh';
      this._scrollOuter.style.paddingTop = '50vh';

      this._stickyWrap = document.createElement('div');
      this._stickyWrap.className = 'dh-rs__sticky';

      // Insert scroll-outer before the header (first content in section)
      var firstContent = header || this.track;
      section.insertBefore(this._scrollOuter, firstContent);
      this._scrollOuter.appendChild(this._stickyWrap);

      // Clip wrapper: contains horizontal card overflow during fan.
      // Must be around track only (NOT on the custom element) to preserve sticky.
      this._clipWrap = document.createElement('div');
      this._clipWrap.className = 'dh-rs__clip-wrapper';

      // Move header, clip-wrapper(track), and cta into the sticky wrapper
      if (header) this._stickyWrap.appendChild(header);
      this._clipWrap.appendChild(this.track);
      this._stickyWrap.appendChild(this._clipWrap);
      if (cta) this._stickyWrap.appendChild(cta);
    }

    _unwrapScrollOuter() {
      if (!this._scrollOuter || !this._stickyWrap) return;

      var section = this._scrollOuter.closest('section');
      if (!section) return;

      // Unwrap track from clip wrapper first
      if (this._clipWrap && this._clipWrap.contains(this.track)) {
        this._clipWrap.parentNode.insertBefore(this.track, this._clipWrap);
        this._clipWrap.remove();
        this._clipWrap = null;
      }

      // Move children back out to section
      while (this._stickyWrap.firstChild) {
        section.insertBefore(this._stickyWrap.firstChild, this._scrollOuter);
      }
      this._scrollOuter.remove();
      this._scrollOuter = null;
      this._stickyWrap = null;
    }

    _setupDesktopScroll() {
      var self = this;

      // Create sticky scroll wrapper
      this._wrapWithScrollOuter();

      // Measure card positions to compute stacking offsets
      // (must happen AFTER wrapping so layout is correct)
      requestAnimationFrame(function () {
        self._computeStackOffsets();

        // Set z-indexes
        self.cards.forEach(function (card, i) {
          card.style.zIndex = self._keyframes[i].z;
        });

        self._setupHover();

        // Initialize progress
        self._rawProgress = self._calcScrollProgress();
        self._smoothedProgress = self._rawProgress;
        self._applyTransforms(self._smoothedProgress * 100);

        // Scroll listener
        self._scrollHandler = function () {
          self._rawProgress = self._calcScrollProgress();
        };
        window.addEventListener('scroll', self._scrollHandler, { passive: true });

        // Resize: recompute offsets
        self._resizeHandler = function () {
          self._computeStackOffsets();
          self._rawProgress = self._calcScrollProgress();
        };
        window.addEventListener('resize', self._resizeHandler, { passive: true });

        // Start rAF loop
        self._isAnimating = true;
        self._animationLoop();
      });
    }

    _calcScrollProgress() {
      if (!this._scrollOuter) return 0;
      var rect = this._scrollOuter.getBoundingClientRect();
      var vh = window.innerHeight;
      var totalTravel = rect.height - vh;
      if (totalTravel <= 0) return 0;
      var traveled = -rect.top;
      return Math.max(0, Math.min(1, traveled / totalTravel));
    }

    _animationLoop() {
      if (!this._isAnimating) return;

      var self = this;

      // Exponential smoothing (factor 0.15 for buttery motion)
      this._smoothedProgress += (this._rawProgress - this._smoothedProgress) * 0.15;

      // Snap to target if close enough
      if (Math.abs(this._rawProgress - this._smoothedProgress) < 0.0001) {
        this._smoothedProgress = this._rawProgress;
      }

      this._applyTransforms(this._smoothedProgress * 100);

      this._rafId = requestAnimationFrame(function () {
        self._animationLoop();
      });
    }

    /* ==================================================================
       Apply transforms based on scroll progress percentage.

       0-15%:   Stacked — all cards overlapping at center
       15-35%:  Fan out — cards spread into downward arc
       35-50%:  Settle — cards flatten from arc into grid row
       50-100%: Hold — cards in natural grid positions (flat row)

       No headline animation — kicker and headline stay put.
       ================================================================== */

    _applyTransforms(pct) {
      if (!this._stackOffsets || this._stackOffsets.length === 0) return;

      for (var i = 0; i < this.cardCount; i++) {
        var kf = this._keyframes[i];
        var card = this.cards[i];
        var stackX = this._stackOffsets[i];
        var fanX = this._fanOffsets[i];
        var fanY = this._fanYOffsets[i];
        var stackR = (i - (this.cardCount - 1) / 2) * 1.5;
        var x, y, r;

        if (pct <= 15) {
          // Phase 1: Stacked at center
          x = stackX;
          y = 0;
          r = stackR;
        } else if (pct <= 35) {
          // Phase 2: Fan out into arc
          var t = (pct - 15) / 20;
          x = this._lerp(stackX, fanX, t);
          y = this._lerp(0, fanY, t);
          r = this._lerp(stackR, kf.fanR, t);
        } else if (pct <= 55) {
          // Phase 3: Settle from arc into flat grid row
          var t2 = (pct - 35) / 20;
          x = this._lerp(fanX, 0, t2);
          y = this._lerp(fanY, 0, t2);
          r = this._lerp(kf.fanR, 0, t2);
        } else {
          // Phase 4: Hold at natural grid positions (no transform)
          x = 0;
          y = 0;
          r = 0;
        }

        var transform = 'translate(' + x.toFixed(1) + 'px, ' + y.toFixed(1) + 'px) rotate(' + r.toFixed(2) + 'deg)';

        if (card._dhHovered) {
          transform += ' translateY(-4px)';
        }

        card.style.transform = transform;
      }
    }

    _lerp(a, b, t) {
      return a + (b - a) * t;
    }

    /* ==================================================================
       Hover
       ================================================================== */

    _setupHover() {
      var self = this;
      this.cards.forEach(function (card) {
        if (card.classList.contains('dh-rs__card--image')) return;

        var enterHandler = function () { card._dhHovered = true; };
        var leaveHandler = function () { card._dhHovered = false; };

        card.addEventListener('mouseenter', enterHandler);
        card.addEventListener('mouseleave', leaveHandler);
        self._hoverHandlers.push({ el: card, enter: enterHandler, leave: leaveHandler });
      });
    }

    /* ==================================================================
       Tablet: Static fan (40% keyframe values)
       ================================================================== */

    _setupStaticFan() {
      var self = this;

      // Compute offsets for static fan positioning
      this._computeStackOffsets();

      this.cards.forEach(function (card, i) {
        var kf = self._keyframes[i];
        var fanX = self._fanOffsets[i];
        var fanY = self._fanYOffsets[i];
        card.style.zIndex = kf.z;
        card.style.transform =
          'translate(' + fanX.toFixed(1) + 'px, ' + fanY.toFixed(1) + 'px) rotate(' + kf.fanR.toFixed(2) + 'deg)';
      });
    }

    /* ==================================================================
       Mobile: Carousel dots
       ================================================================== */

    _setupMobileCarousel() {
      if (!this.track || this.cards.length === 0) return;

      var self = this;
      this._scrollTimer = null;

      // Scroll-position-based tracking (replaces IntersectionObserver)
      this._carouselScrollHandler = function () {
        if (self._scrollTimer) cancelAnimationFrame(self._scrollTimer);
        self._scrollTimer = requestAnimationFrame(function () {
          self._detectActiveCardFromScroll();
        });
      };
      this.track.addEventListener('scroll', this._carouselScrollHandler, { passive: true });

      // Dot click handlers
      this.dots.forEach(function (dot, i) {
        var handler = function () { self._scrollCarouselTo(i); };
        dot.addEventListener('click', handler);
        self._dotHandlers.push({ el: dot, handler: handler });
      });

      // Set initial active
      this._setActiveDot(0);
    }

    _detectActiveCardFromScroll() {
      if (!this.track || !this.cards.length) return;

      var trackRect = this.track.getBoundingClientRect();
      var trackCenter = trackRect.left + trackRect.width / 2;
      var closest = 0;
      var closestDist = Infinity;

      for (var i = 0; i < this.cards.length; i++) {
        var cardRect = this.cards[i].getBoundingClientRect();
        var cardCenter = cardRect.left + cardRect.width / 2;
        var dist = Math.abs(cardCenter - trackCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      }

      this._setActiveDot(closest);
    }

    _scrollCarouselTo(index) {
      if (!this.track || !this.cards[index]) return;

      var card = this.cards[index];
      var containerRect = this.track.getBoundingClientRect();
      var cardRect = card.getBoundingClientRect();

      var scrollLeft = this.track.scrollLeft +
        (cardRect.left - containerRect.left) -
        (containerRect.width - cardRect.width) / 2;

      this.track.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
      this._setActiveDot(index);
    }

    _setActiveDot(index) {
      this._activeDotIndex = index;
      this.dots.forEach(function (dot, i) {
        if (i === index) {
          dot.classList.add('dh-rs__dot--active');
        } else {
          dot.classList.remove('dh-rs__dot--active');
        }
      });
    }
  }

  customElements.define('dh-reviews-scroll', DhReviewsScroll);
}
